import { gql } from '@apollo/client';

import { fragments as patientFragments } from './patients';
import { fragments as staffFragments } from './staff';
import { fragments as bookingFragments } from './bookings';
import { fragments as anesthesiologistFragments } from './anesthesiologists';

// Minimal fragment for schedule display (only essential fields)
const roomDataSchedule = gql`
  fragment RoomDataSchedule on Room {
    id
    _fetchedAt
    name
    type
    isHideScheduleScreen
    order
  }
`;

export const fragments = {
  m: gql`
    fragment RoomDataM on Room {
      id
      name
    }
  `,
  roomDataSchedule,
  s: gql`
    fragment RoomDataS on Room {
      id
      name
      type
      capacity
      availability
      dailyAvailability {
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        sunday
      }
      openType
      isAlphabeticBedOrder
      isHideInternalScreen
      isHideScheduleScreen
      isHideLiaisonScreen
      hideTurnoverClock
      airFiltrationEndsAt
      airFiltrationWarningOn
      airFiltrationDurationMin
      cleaningStartedAt
      turnoverStartedAt
      procedureStartedAt
      warningThreshold
      oobWarningThreshold
      ptWarningThreshold
      ptCompleteWarningThreshold
      pacuOutWarningThreshold
      alertThreshold
      order
      orRoomReady
      orRoomReadyAt
    }
  `,
  all: gql`
    fragment RoomData on Room {
      id
      name
      type
      capacity
      availability
      dailyAvailability {
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        sunday
      }
      openType
      isAlphabeticBedOrder
      isHideInternalScreen
      isHideScheduleScreen
      isHideLiaisonScreen
      hideTurnoverClock
      airFiltrationEndsAt
      airFiltrationWarningOn
      airFiltrationDurationMin
      cleaningStartedAt
      turnoverStartedAt
      procedureStartedAt
      warningThreshold
      oobWarningThreshold
      ptWarningThreshold
      ptCompleteWarningThreshold
      pacuOutWarningThreshold
      alertThreshold
      order
      orRoomReady
      orRoomReadyAt
    }
  `,
};

export const list = gql`
  query rooms {
    rooms {
      ...RoomDataS
    }
  }
  ${fragments.s}
`;

export const listXS = gql`
  query rooms {
    rooms {
      id
      name
      type
    }
  }
`;

export const listNursing = gql`
  subscription rooms {
    rooms {
      ...RoomDataS
      patient {
        id
      }
    }
  }
  ${fragments.s}
`;

export const patientRooms = gql`
  query patientRooms {
    rooms: patientRooms {
      ...RoomDataS
    }
  }
  ${fragments.s}
`;

export const notUtilityRooms = gql`
  query notUtilityRooms {
    rooms: notUtilityRooms {
      ...RoomDataS
    }
  }
  ${fragments.s}
`;

export const operationRooms = gql`
  query operationRooms {
    rooms: operationRooms {
      ...RoomDataS
    }
  }
  ${fragments.s}
`;

export const item = gql`
  query room($id: Long!) {
    room(id: $id) {
      ...RoomData
    }
  }
  ${fragments.all}
`;

export const create = gql`
  mutation createRoom(
    $name: String!
    $type: RoomType!
    $capacity: Int
    $availability: Float
    $dailyAvailability: DailyAvailabilityInput
    $openType: Boolean
    $airFiltrationDurationMin: Int
    $isAlphabeticBedOrder: Boolean
    $isHideInternalScreen: Boolean
    $isHideScheduleScreen: Boolean
    $isHideLiaisonScreen: Boolean
    $hideTurnoverClock: Boolean
    $warningThreshold: Int
    $oobWarningThreshold: Int
    $ptWarningThreshold: Int
    $ptCompleteWarningThreshold: Int
    $pacuOutWarningThreshold: Int
    $alertThreshold: Int
    $order: Int
  ) {
    createRoom(
      name: $name
      type: $type
      capacity: $capacity
      availability: $availability
      dailyAvailability: $dailyAvailability
      openType: $openType
      airFiltrationDurationMin: $airFiltrationDurationMin
      isAlphabeticBedOrder: $isAlphabeticBedOrder
      isHideInternalScreen: $isHideInternalScreen
      isHideScheduleScreen: $isHideScheduleScreen
      isHideLiaisonScreen: $isHideLiaisonScreen
      hideTurnoverClock: $hideTurnoverClock
      warningThreshold: $warningThreshold
      oobWarningThreshold: $oobWarningThreshold
      ptWarningThreshold: $ptWarningThreshold
      ptCompleteWarningThreshold: $ptCompleteWarningThreshold
      pacuOutWarningThreshold: $pacuOutWarningThreshold
      alertThreshold: $alertThreshold
      order: $order
    ) {
      ...RoomDataS
    }
  }
  ${fragments.s}
`;

export const update = gql`
  mutation editRoom(
    $id: Long!
    $name: String!
    $type: RoomType!
    $capacity: Int
    $availability: Float
    $dailyAvailability: DailyAvailabilityInput
    $openType: Boolean
    $airFiltrationDurationMin: Int
    $isAlphabeticBedOrder: Boolean
    $isHideInternalScreen: Boolean
    $isHideScheduleScreen: Boolean
    $isHideLiaisonScreen: Boolean
    $hideTurnoverClock: Boolean
    $warningThreshold: Int
    $oobWarningThreshold: Int
    $ptWarningThreshold: Int
    $ptCompleteWarningThreshold: Int
    $pacuOutWarningThreshold: Int
    $alertThreshold: Int
    $order: Int
  ) {
    editRoom(
      id: $id
      name: $name
      type: $type
      capacity: $capacity
      availability: $availability
      dailyAvailability: $dailyAvailability
      openType: $openType
      airFiltrationDurationMin: $airFiltrationDurationMin
      isAlphabeticBedOrder: $isAlphabeticBedOrder
      isHideInternalScreen: $isHideInternalScreen
      isHideScheduleScreen: $isHideScheduleScreen
      isHideLiaisonScreen: $isHideLiaisonScreen
      hideTurnoverClock: $hideTurnoverClock
      warningThreshold: $warningThreshold
      oobWarningThreshold: $oobWarningThreshold
      ptWarningThreshold: $ptWarningThreshold
      ptCompleteWarningThreshold: $ptCompleteWarningThreshold
      pacuOutWarningThreshold: $pacuOutWarningThreshold
      alertThreshold: $alertThreshold
      order: $order
    ) {
      ...RoomDataS
    }
  }
  ${fragments.s}
`;

export const remove = gql`
  mutation deleteRoom($id: Long!) {
    deleteRoom(id: $id)
  }
`;

export const addAnesthesiologistShiftRoom = gql`
  mutation addAnesthesiologistShiftRoom($id: Long!, $date: Date!, $anesthesiologistId: Long!, $roomTypeString: String) {
    addAnesthesiologistShiftRoom(
      roomId: $id
      date: $date
      anesthesiologistId: $anesthesiologistId
      roomTypeString: $roomTypeString
    )
  }
`;

export const removeAnesthesiologistShiftRoom = gql`
  mutation removeAnesthesiologistShiftRoom($id: Long!, $roomTypeString: String) {
    removeAnesthesiologistShiftRoom(id: $id, roomTypeString: $roomTypeString)
  }
`;

export const addSecondaryAnesthesiologistShiftRoom = gql`
  mutation addSecondaryAnesthesiologistShiftRoom(
    $id: Long!
    $date: Date!
    $anesthesiologistId: Long!
    $roomTypeString: String
  ) {
    addSecondaryAnesthesiologistShiftRoom(
      roomId: $id
      date: $date
      anesthesiologistId: $anesthesiologistId
      roomTypeString: $roomTypeString
    )
  }
`;

export const removeSecondaryAnesthesiologistShiftRoom = gql`
  mutation removeSecondaryAnesthesiologistShiftRoom($id: Long!) {
    removeSecondaryAnesthesiologistShiftRoom(id: $id)
  }
`;

export const listSubscription = gql`
  subscription {
    internalMonitor {
      id
      _fetchedAt
      name
      type
      capacity
      awaitingCleanup
      airFiltrationEndsAt
      airFiltrationWarningOn
      airFiltrationDurationMin
      cleaningStartedAt
      turnoverStartedAt
      procedureStartedAt
      isHideInternalScreen
      isHideLiaisonScreen
      hideTurnoverClock
      warningThreshold
      oobWarningThreshold
      ptWarningThreshold
      ptCompleteWarningThreshold
      pacuOutWarningThreshold
      alertThreshold
      order
      orRoomReady
      orRoomReadyAt
      procedure {
        id
        startTime
        duration
        orId
        patientId
        isCanceled
        color
        patient {
          dischargedAt
          room {
            type
          }
        }
        physician {
          id
          name
          currentRoomId
          room {
            id
            name
          }
        }
      }
      patient {
        id
        name
        initials
        type
        procedure {
          id
          startTime
          duration
        }
        procedureType {
          id
          name
        }
        physician {
          id
          name
          notificationNumbers
          shouldSendNotification
        }
        icon
        color
        status
        createdAt
        changedRoomAt
        called
        caretakerConsent
        caretakerName
        caretakerNote
        notificationNumbers
        physicianLastPatient
        anesthesiologistLastPatient
        medsDeliveryStatus
        caretakerThread {
          id
          hasUnreadMessages
        }
        ...PatientLog
      }
      patients {
        id
        name
        initials
        type
        bed
        pacuBed
        priority
        note
        preOpNote
        pacuNote
        blockNerveFollow
        procedure {
          startTime
        }
        procedureType {
          id
          name
          expectedPreOpDuration
          expectedDuration
          expectedPacuDuration
        }
        physician {
          id
          name
          notificationNumbers
          shouldSendNotification
        }
        caretakerMessages {
          phoneNumber
          content
        }
        createdAt
        changedRoomAt
        status
        called
        caretakerConsent
        caretakerName
        caretakerNote
        notificationNumbers
        physicianLastPatient
        anesthesiologistLastPatient
        medsDeliveryStatus
        caretakerThread {
          id
          hasUnreadMessages
        }
        transferredToChair
        transferredToPT
        ptComplete
        preOpNurse {
          id
          staffId
          name
          primarySpecialty
        }
        pacuNurse {
          id
          staffId
          name
          primarySpecialty
        }
        room {
          id
          name
          type
          oobWarningThreshold
          ptWarningThreshold
          ptCompleteWarningThreshold
          pacuOutWarningThreshold
        }
        ...PatientLog
      }
    }
  }
  ${patientFragments.patientLog}
`;

export const listOperationRoomsSubscription = gql`
  subscription {
    internalMonitor(orOnly: true) {
      id
      name
      orRoomReady
      patient {
        id
        name
        physician {
          id
          name
        }
        icon
        color
        status
        pacuBed
        type
      }
    }
  }
`;

export const opreationRoomSubscription = gql`
  subscription tabletApp($roomId: Long!) {
    tabletApp(roomId: $roomId) {
      id
      name
      type
      airFiltrationEndsAt
      airFiltrationWarningOn
      airFiltrationDurationMin
      cleaningStartedAt
      turnoverStartedAt
      procedureStartedAt
      order
      orRoomReady
      orRoomReadyAt
      patient {
        id
        name
        type
        procedureType {
          id
          name
        }
        physician {
          id
          name
          currentRoomId
          notificationNumbers
          shouldSendNotification
          room {
            id
            name
          }
        }
        icon
        initials
        color
        caretakerConsent
        caretakerName
        caretakerNote
        notificationNumbers
        caretakerThread {
          id
          hasUnreadMessages
        }
        status
        createdAt
        changedRoomAt
        procedure {
          id
          anesthesiologist {
            id
            name
          }
          anesthesiaType
          orId
        }
        pacuBed
        ...PatientLog
      }
      patients {
        id
        name
        type
        procedureType {
          id
          name
        }
        physician {
          id
          name
        }
        icon
        color
        status
        createdAt
        changedRoomAt
      }
      awaitingCleanup
    }
  }
  ${patientFragments.patientLog}
`;

export const scheduleSubscription = gql`
  subscription {
    scheduleMonitor {
      ...RoomDataS
    }
  }
  ${fragments.s}
`;

export const startTimeout = gql`
  mutation startTimeout($id: Long!) {
    startTimeout(id: $id)
  }
`;

export const patientReadyInOR = gql`
  mutation patientReadyInOR($id: Long!) {
    patientReadyInOR(id: $id)
  }
`;

export const startProcedure = gql`
  mutation startProcedure($id: Long!) {
    startProcedure(id: $id)
  }
`;

export const startClosing = gql`
  mutation startClosing($id: Long!) {
    startClosing(id: $id)
  }
`;

export const startDressingOn = gql`
  mutation startDressingOn($id: Long!) {
    startDressingOn(id: $id)
  }
`;

export const completeCleaning = gql`
  mutation completeCleaning($id: Long!) {
    completeCleaning(id: $id)
  }
`;

export const orRoomReady = gql`
  mutation orRoomReady($id: Long!) {
    orRoomReady(id: $id)
  }
`;

export const skipCleaning = gql`
  mutation skipCleaning($id: Long!) {
    skipCleaning(id: $id)
  }
`;

export const turnoverClockOff = gql`
  mutation turnoverClockOff($id: Long!) {
    turnoverClockOff(id: $id)
  }
`;

export const markAsComplete = gql`
  mutation markAsComplete($patientId: Long!) {
    markAsComplete(patientId: $patientId)
  }
`;

export const setPatientStatus = gql`
  mutation setPatientStatus($id: Long!, $status: PatientStatus!) {
    setPatientStatus(id: $id, status: $status)
  }
`;

export const roomsForGateway = gql`
  query roomsForGateway($hospitalId: Long) {
    rooms: roomsForGateway(hospitalId: $hospitalId) {
      ...RoomDataM
    }
  }
  ${fragments.m}
`;

// Schedule-specific subscription with minimal fragments (optimized for schedule display only)
export const scheduleRoomsSubscription = gql`
  subscription rooms($date: Date!) {
    rooms {
      ...RoomDataSchedule
      staffShifts(date: $date) {
        ...StaffShiftDataSchedule
      }
      bookings(date: $date) {
        ...BookingDataSchedule
      }
      anesthesiologistShifts(date: $date) {
        ...AnesthesiologistShiftRoomDataSchedule
      }
      secondaryAnesthesiologistShifts(date: $date) {
        ...AnesthesiologistShiftRoomDataSchedule
      }
    }
  }
  ${fragments.roomDataSchedule}
  ${staffFragments.shiftSchedule}
  ${bookingFragments.bookingDataSchedule}
  ${anesthesiologistFragments.shiftSchedule}
`;

export const restartAirFiltration = gql`
  mutation restartAirFiltration($id: Long!) {
    restartAirFiltration(id: $id)
  }
`;

export const roomSubscription = gql`
  subscription tabletApp($roomId: Long!) {
    room: tabletApp(roomId: $roomId) {
      ...RoomDataS
    }
  }
  ${fragments.s}
`;

export const scheduleUserRoomShifts = gql`
  subscription scheduleUserRoomShifts($scheduleUserId: Long!, $scheduleUserType: String!, $offset: Int!, $limit: Int!) {
    scheduleUserRoomShifts(
      scheduleUserId: $scheduleUserId
      scheduleUserType: $scheduleUserType
      offset: $offset
      limit: $limit
    ) {
      totalCount
      entities {
        ...StaffShiftRoomData
      }
      hasNextPage
    }
  }
  ${staffFragments.roomShift}
`;

export const setRoomShiftState = gql`
  mutation setRoomShiftState($shiftId: Long!, $scheduleUserType: String!, $scheduleUserId: Long!, $state: ShiftState!) {
    setRoomShiftState(
      shiftId: $shiftId
      scheduleUserType: $scheduleUserType
      scheduleUserId: $scheduleUserId
      state: $state
    )
  }
`;

export default {
  list,
  item,
  create,
  update,
  remove,
  listSubscription,
};
